/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import {NgForm} from '@angular/forms';
import {UIRouter} from '@uirouter/core';
import {Subscription} from 'rxjs';
import * as _ from 'lodash';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {UpdateSession} from '../../../components/session/update-session';
import {buildElectionFormOptions, buildElectionSelectGroups} from '../../../components/util/election-form-options';
import {adnFormHelper} from '../../common/controller/form-helper';
import {
  clearFormValidation,
  createFormSubmitState,
  exposeFormSubmitStateAccessors,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  hasFormValidationError,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../../common/controller/form-submit-state';
import {
  budgetSetUp,
  convertToApiBudgets,
  convertToApiEntities,
  convertToUiBudgets,
  convertToUiEntities,
  doTheCopyOfEntity
} from './advertiser-budgets';
import template from './update-advertiser-page.component.html';
import {AdvertiserNoAuthResource} from '../../../components/api/resources/resource/advertiser-no-auth.resource';
import {ElectionNoAuthResource} from '../../../components/api/resources/resource/election-no-auth.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {BrowserLocationService} from '../../angular-platform-providers';
import {compareById} from '../../common/util/object-id-comparison';

@Component({
  standalone: false,
  selector: 'adn-update-advertiser-page',
  template
})
export class UpdateAdvertiserPageComponent implements OnInit, OnDestroy {
  readonly entityTypes = ['CORPORATE_ENTITY_PUBLIC', 'CORPORATE_ENTITY_PRIVATE', 'POLITICAL_PARTY', 'INDIVIDUAL'];

  cancel: (form?: NgForm) => void;
  editsMade: (form?: NgForm) => void;
  saveDirect: (resource: any) => void;
  setForm: (form: NgForm) => void;
  getUnderlyingModel: () => any;

  model: any;
  entities: any[] = [];
  elections: any[] = [];
  electionSelectGroups: any = {dated: [], undated: [], useGroups: false, groups: []};
  availableCurrencies: string[] = [];
  addElectionBudget: () => void;
  removeElection: (index: number) => void;
  removeCampaign: (budget: any, index: number) => void;
  addCampaign: (budget: any) => void;
  removeEntity: (index: number) => void;
  addEntity: () => void;
  done = false;
  missingAdvertiser = false;
  submitState: FormSubmitState = createFormSubmitState();
  pending = false;

  private rawElections: any[] = [];
  private underlyingModel: any;
  private _registeredForm: NgForm | null = null;
  private _formSub: Subscription | null = null;
  private _formSettleTimer: any = null;
  private _initializingForm = false;
  private _angularFormDirty = false;
  private readonly formControlErrors: FormControlError[] = [];

  constructor(
    private readonly eventBus: AppEventBusService,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(AdvertiserNoAuthResource) private readonly AdvertiserNoAuth: any,
    @Inject(ElectionNoAuthResource) private readonly ElectionNoAuth: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly ngZone: NgZone,
    private readonly uiRouter: UIRouter
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState, {
      getRegisteredForm: () => this._registeredForm
    });
  }

  ngOnInit(): void {
    const routeParams = this.uiRouter.globals.params;
    if (routeParams.context) {
      UpdateSession.obtain().init(routeParams.context, routeParams.auth_token);
    }

    budgetSetUp(this.location, this as any);

    this.ElectionNoAuth.get({includeTests: false}, (data: any) => {
      this.ngZone.run(() => {
        this.rawElections = data.results || [];
        this.refreshElections();
      });
    });

    const advertiserId = routeParams.id || 'missing';
    this.AdvertiserNoAuth.get({id: advertiserId}, (model: any) => {
      this.ngZone.run(() => {
        this.done = true;
        this.setUpResourceForm(model);
      });
    }, () => {
      this.ngZone.run(() => {
        this.missingAdvertiser = true;
        this.done = true;
      });
    });
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
  }

  registerForm(editForm: NgForm): string | null {
    if (this._registeredForm !== editForm) {
      this._registeredForm = editForm;
      this.setForm(editForm);
      this._startTrackingForm();
    }
    return null;
  }

  submit(editForm: NgForm): void {
    if (this.pending || this.isSaveDisabled(editForm)) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);
    this.onBeforeSubmit();
    Object.assign(this.underlyingModel, this.model);

    this.underlyingModel.savePartial(null, 'save', null).then((resource: any) => {
      this.ngZone.run(() => {
        updateFormWarnings(this.submitState, resource || this.model, this.LocalNetworkProfile.isPureMarketplacePlatform());
        this.saveDirect(resource);
        markFormSubmitSuccess(this.submitState, editForm);
        this._angularFormDirty = false;
        this._startTrackingForm();
      });
    }, (response: any) => {
      if ((response.status === 400 || response.status === 403 || response.status === 422)
        && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
        handleApiValidationErrors(
          this.submitState,
          this.model,
          _.get(response, ['data', 'errors']) || response.data,
          this.formControlErrors,
          editForm
        );
      }
      markFormSubmitError(this.submitState, editForm);
    }).finally(() => {
      this.pending = false;
      this.eventBus.emit('SUBMIT_PROCESSED');
    });
  }

  isFormPristine(editForm: NgForm): boolean {
    void editForm;
    return !this._angularFormDirty;
  }

  isSaveDisabled(editForm: NgForm): boolean {
    return this.isFormPristine(editForm)
      || this.pending
      || (editForm.invalid && !hasFormValidationError(this.submitState));
  }

  readonly compareById = compareById;

  trackByIndex(index: number): number {
    return index;
  }

  copyToEntity(entity: any): void {
    this.editsMade();
    doTheCopyOfEntity(this.model, entity);
  }

  onBeforeSubmit(): void {
    this.model.politicalBudgets = convertToApiBudgets(this.model.uiPoliticalBudgets);
    convertToApiEntities(this.model);
  }

  private setUpResourceForm(model: any): void {
    this.underlyingModel = model;
    this._initializingForm = true;
    adnFormHelper.setUpForm(this, model, {
      afterInit: () => {
        this.model.type = this.model.type || null;
        this.model.address = this.model.address || {};
      },
      afterSave: () => {
      },
      afterInitAfterSave: (saveObj: any) => {
        const workingObj = saveObj || this.model;
        this.model.uiPoliticalBudgets = convertToUiBudgets(workingObj);
        convertToUiEntities(workingObj);
        [this.model.controllingEntity, this.model.payingEntity].forEach((entity: any) => {
          entity.address = entity.address || {};
        });
        this.entities = [this.model.controllingEntity, this.model.payingEntity];
        this.refreshElections();
      }
    });

    this.editsMade = () => {
      if (this._initializingForm) {
        return;
      }
      this._registeredForm?.control.markAsDirty();
      this._angularFormDirty = true;
    };

    this.setForm = (editForm: NgForm) => {
      this._registeredForm = editForm;
    };

    const resetModel = this.cancel;
    this.cancel = (editForm: NgForm) => {
      if (this.isFormPristine(editForm) || this.pending || (editForm.invalid && !hasFormValidationError(this.submitState))) {
        return;
      }
      this._initializingForm = true;
      resetModel(editForm);
      this._angularFormDirty = false;
      if (editForm) {
        editForm.control.markAsPristine();
      }
      this._initializingForm = false;
      this._startTrackingForm();
    };

    this._initializingForm = false;
  }

  private refreshElections(): void {
    this.elections = buildElectionFormOptions(this.rawElections || [], {
      selectedElectionIds: ((this.model && this.model.uiPoliticalBudgets) || []).map((budget: any) => {
        return budget.election && budget.election.id;
      }),
      onlyFuture: true
    });
    this.electionSelectGroups = buildElectionSelectGroups(this.elections);
  }

  private _startTrackingForm(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    const form = this._registeredForm;
    if (!form) {
      return;
    }
    this._formSettleTimer = setTimeout(() => {
      this._formSub = form.form?.valueChanges?.subscribe(() => {
        if (!this._initializingForm) {
          this._angularFormDirty = true;
        }
      }) || null;
    }, 0);
  }
}
